import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { BRAND, NAV, CONTACT_LINKS, PORTAL_LINKS } from "../../lib/site-data";

/** Map hash links to home sections; pass through absolute app routes. */
function hrefToTo(href) {
  if (!href) return "/";
  if (href.startsWith("#")) {
    return { pathname: "/", hash: href.slice(1) };
  }
  return href;
}

export default function Navbar() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(!isHome);
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return;
    }
    setScrolled(window.scrollY > 12);
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome, pathname]);

  const linkTone = scrolled
    ? "text-ink/80 hover:text-ink hover:bg-ink/5"
    : "text-cream/90 hover:text-cream hover:bg-white/10";

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex h-[72px] items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className={`group inline-flex items-center gap-2.5 transition-colors ${
            scrolled ? "text-ink" : "text-cream"
          }`}
        >
          <img
            src={BRAND.logoUrl}
            alt=""
            aria-hidden="true"
            className="h-9 w-9 rounded-2xl object-contain"
          />
          <span className="font-display text-[18px] font-extrabold tracking-tight">
            {BRAND.name}
          </span>
        </Link>

        <nav
          aria-label="Primary"
          className="hidden lg:flex items-center gap-1"
        >
          {NAV.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => setOpenMenu(item.label)}
              onMouseLeave={() => setOpenMenu(null)}
            >
              {item.children ? (
                <button
                  type="button"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-[15px] font-medium transition-colors ${linkTone}`}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              ) : (
                <Link
                  to={hrefToTo(item.href)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`inline-flex items-center rounded-full px-4 py-2 text-[15px] font-medium transition-colors ${linkTone}`}
                >
                  {item.label}
                </Link>
              )}

              {item.children && openMenu === item.label && (
                <div
                  className="absolute left-0 top-full pt-3"
                  data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
                >
                  <div className="min-w-[260px] rounded-2xl bg-white p-2 shadow-[0_18px_44px_rgba(14,35,51,0.12)] border border-border/60">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={hrefToTo(c.href)}
                        className="block rounded-xl px-3.5 py-2.5 text-[14.5px] text-ink/80 hover:bg-cream hover:text-forest transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={PORTAL_LINKS.patientSignup}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-patient-signup"
            className={`text-[14.5px] font-medium underline-offset-4 hover:underline transition-colors ${
              scrolled ? "text-ink/80 hover:text-ink" : "text-cream/90 hover:text-cream"
            }`}
          >
            Sign up
          </a>
          <a
            href={CONTACT_LINKS.whatsapp.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-get-care-button"
            className="inline-flex items-center justify-center rounded-full bg-forest hover:bg-forestHover text-cream px-5 py-2.5 text-[14.5px] font-semibold shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
          >
            Get care now
          </a>
        </div>

        <button
          aria-label="Toggle navigation"
          data-testid="mobile-nav-toggle"
          className={`lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
            scrolled ? "text-ink hover:bg-ink/5" : "text-cream hover:bg-white/10"
          }`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-nav-panel"
          className="lg:hidden border-t border-border/60 bg-cream/95 backdrop-blur-xl"
        >
          <div className="container-x py-4 flex flex-col">
            {NAV.flatMap((item) =>
              item.children
                ? [
                    <div key={item.label} className="pt-2 pb-1 text-[12px] uppercase tracking-[0.16em] text-ink/50 font-semibold">
                      {item.label}
                    </div>,
                    ...item.children.map((c) => (
                      <Link
                        key={`${item.label}-${c.label}`}
                        to={hrefToTo(c.href)}
                        onClick={() => setOpen(false)}
                        className="py-2.5 text-ink/85 hover:text-forest"
                      >
                        {c.label}
                      </Link>
                    )),
                  ]
                : [
                    <Link
                      key={item.label}
                      to={hrefToTo(item.href)}
                      onClick={() => setOpen(false)}
                      className="py-3 text-[16px] font-medium text-ink hover:text-forest"
                    >
                      {item.label}
                    </Link>,
                  ]
            )}
            <a
              href={PORTAL_LINKS.patientSignup}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center rounded-full border border-forest text-forest px-5 py-3 text-[15px] font-semibold"
              data-testid="mobile-nav-signup"
            >
              Patient sign up
            </a>
            <a
              href={CONTACT_LINKS.whatsapp.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center rounded-full bg-forest text-cream px-5 py-3 text-[15px] font-semibold"
              data-testid="mobile-nav-cta"
            >
              Get care now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
